/**
 * EVERY 401 FROM OUR BACKEND, caught in one place — ported from tkt-back-office
 * lib/sessionExpired.js. One wrapper around globalThis.fetch (better-auth's client resolves fetch
 * per request, so it is covered too).
 *
 *  1. If this machine has an enrolled passkey and a platform authenticator, re-authenticate
 *     silently (one shared ceremony however many requests 401'd at once) and replay the request.
 *  2. Otherwise raise one sticky "Session expired" toast with a Sign in action — never a
 *     takeover that throws away what the operator had half-typed.
 *
 * Caveat the backend imposes: several routes answer a missing PERMISSION with 401 rather than 403
 * (tkt-backend checkPermission). Only our backend's 401s count, and sign-in routes are excluded.
 */
import { getBackendUrl } from './backendUrl.js';
import { passkeySupport, readPasskeyHint, signInWithPasskey } from './passkey.js';
import { clearSessionMarker } from './session.js';
import { toast, TOAST_BUS } from './toast.js';

let announced = false;
let reauthInFlight = null;

const isCredentialCheck = (url) => /\/sign-in|\/sign-up|\/sign-out|\/email-otp|\/get-session|\/passkey/.test(url);

const signOutAndReload = async () => {
  clearSessionMarker();
  try {
    await fetch(`${getBackendUrl()}/api/auth/sign-out`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
  } catch { /* already dead */ }
  window.location.reload();
};

export const handleUnauthorized = (url = '') => {
  if (announced || isCredentialCheck(url)) return;
  announced = true;
  toast('Session expired', {
    tone: 'error',
    action: { label: 'Sign in', onClick: signOutAndReload },
  });
  TOAST_BUS.suppressErrors = true;
};

const canReauth = async () => {
  if (!readPasskeyHint()) return false;
  const s = await passkeySupport().catch(() => null);
  return !!(s && s.supported && s.platform);
};

const reauthenticate = () => {
  if (!reauthInFlight) {
    reauthInFlight = (async () => {
      try { await signInWithPasskey(); return true; } catch { return false; } finally { setTimeout(() => { reauthInFlight = null; }, 0); }
    })();
  }
  return reauthInFlight;
};

export const installSessionGuard = () => {
  if (typeof globalThis === 'undefined' || globalThis.__momoSessionGuard) return;
  globalThis.__momoSessionGuard = true;
  const base = getBackendUrl();
  const realFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = async (input, init) => {
    const response = await realFetch(input, init);
    try {
      if (response.status === 401) {
        const url = String(typeof input === 'string' ? input : input?.url || response.url || '');
        const ours = url.startsWith(base);
        if (ours && !isCredentialCheck(url) && !init?.__reauthRetry && (await canReauth()) && (await reauthenticate())) {
          return realFetch(input, { ...(init || {}), __reauthRetry: true });
        }
        if (ours) handleUnauthorized(url);
      }
    } catch { /* never let the guard break a real request */ }
    return response;
  };
};
